/**
 * Meeting storage operations for offline meetings
 * Handles saving, retrieving, and managing offline meetings with quota management
 * Following OBY offline-patterns.json: UUID for offline IDs, sync status tracking
 */

import { v4 as uuidv4 } from "uuid";
import type { MeetingFormData } from "@/types/meeting.types";
import {
  addToSyncQueue,
  deleteOfflineMeeting,
  getAllOfflineMeetings,
  getOfflineMeeting,
  getOfflineMeetingsByCustomer,
  getOfflineMeetingsByStatus,
  saveOfflineMeeting,
  updateMeetingStatus,
} from "./db";
import { withQuotaHandling } from "./quota-handler";
import type { OfflineMeeting, SyncQueueItem } from "./types";

/**
 * Create and save a new offline meeting
 * Automatically adds to sync queue
 */
export async function createOfflineMeeting(meetingData: MeetingFormData): Promise<{
  success: boolean;
  meeting?: OfflineMeeting;
  error?: string;
}> {
  try {
    const now = new Date().toISOString();
    const meetingId = uuidv4();

    const offlineMeeting: OfflineMeeting = {
      id: meetingId,
      customer_id: meetingData.customer_id,
      project_id: meetingData.project_id || null,
      meeting_date: meetingData.meeting_date,
      meeting_time: meetingData.meeting_time,
      duration_minutes: meetingData.duration_minutes || 60,
      event_type: meetingData.event_type || "meeting",
      meeting_type: meetingData.meeting_type || "consultation",
      status: meetingData.status || "scheduled",
      location: meetingData.location || null,
      notes: meetingData.notes || null,
      created_at: now,
      updated_at: now,
      sync_status: "pending",
      retry_count: 0,
      offline_created: true,
    };

    await withQuotaHandling(() => saveOfflineMeeting(offlineMeeting));

    // Add to sync queue
    const queueItem: SyncQueueItem = {
      id: meetingId,
      type: "meeting",
      data: offlineMeeting,
      created_at: now,
      retry_count: 0,
    };

    await withQuotaHandling(() => addToSyncQueue(queueItem));

    console.log("[Meeting Storage] Offline meeting created:", meetingId);
    return { success: true, meeting: offlineMeeting };
  } catch (error) {
    console.error("[Meeting Storage] Error creating offline meeting:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Failed to create offline meeting";
    return { success: false, error: errorMessage };
  }
}

/**
 * Update an existing offline meeting
 */
export async function updateOfflineMeeting(
  meetingId: string,
  updates: Partial<MeetingFormData>
): Promise<{
  success: boolean;
  meeting?: OfflineMeeting;
  error?: string;
}> {
  try {
    const existing = await getOfflineMeeting(meetingId);
    if (!existing) {
      return { success: false, error: "Meeting not found" };
    }

    const updatedMeeting: OfflineMeeting = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString(),
      sync_status: "pending", // Mark as needing sync
    };

    await withQuotaHandling(() => saveOfflineMeeting(updatedMeeting));

    // Update sync queue
    const queueItem: SyncQueueItem = {
      id: meetingId,
      type: "meeting",
      data: updatedMeeting,
      created_at: existing.created_at,
      retry_count: existing.retry_count || 0,
    };

    await withQuotaHandling(() => addToSyncQueue(queueItem));

    console.log("[Meeting Storage] Offline meeting updated:", meetingId);
    return { success: true, meeting: updatedMeeting };
  } catch (error) {
    console.error("[Meeting Storage] Error updating offline meeting:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Failed to update offline meeting";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get a specific offline meeting by ID
 */
export async function getStoredOfflineMeeting(
  id: string
): Promise<{ success: boolean; meeting?: OfflineMeeting; error?: string }> {
  try {
    const meeting = await getOfflineMeeting(id);
    if (!meeting) {
      return { success: false, error: "Meeting not found" };
    }
    return { success: true, meeting };
  } catch (error) {
    console.error("[Meeting Storage] Error getting offline meeting:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to get offline meeting";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all offline meetings
 */
export async function getOfflineMeetings(): Promise<{
  success: boolean;
  meetings?: OfflineMeeting[];
  error?: string;
}> {
  try {
    const meetings = await getAllOfflineMeetings();
    return { success: true, meetings };
  } catch (error) {
    console.error("[Meeting Storage] Error getting offline meetings:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to get offline meetings";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get meetings for a specific customer
 */
export async function getCustomerOfflineMeetings(customerId: number): Promise<OfflineMeeting[]> {
  try {
    return await getOfflineMeetingsByCustomer(customerId);
  } catch (error) {
    console.error("[Meeting Storage] Error getting customer meetings:", error);
    return [];
  }
}

/**
 * Get meetings pending sync (not yet uploaded)
 */
export async function getPendingSyncMeetings(): Promise<OfflineMeeting[]> {
  try {
    return await getOfflineMeetingsByStatus("pending");
  } catch (error) {
    console.error("[Meeting Storage] Error getting pending sync meetings:", error);
    return [];
  }
}

/**
 * Get count of meetings pending sync
 */
export async function getPendingSyncCount(): Promise<number> {
  try {
    const meetings = await getPendingSyncMeetings();
    return meetings.length;
  } catch (error) {
    console.error("[Meeting Storage] Error getting pending sync count:", error);
    return 0;
  }
}

/**
 * Mark meeting as syncing (upload in progress)
 */
export async function markMeetingSyncing(id: string): Promise<void> {
  try {
    await updateMeetingStatus(id, "syncing");
  } catch (error) {
    console.error("[Meeting Storage] Error marking meeting as syncing:", error);
    throw error;
  }
}

/**
 * Mark meeting as synced (successfully uploaded)
 * Removes from offline storage and sync queue
 */
export async function markMeetingSynced(id: string): Promise<void> {
  try {
    await updateMeetingStatus(id, "synced");
    // Note: Actual deletion happens in sync-manager after confirmation
  } catch (error) {
    console.error("[Meeting Storage] Error marking meeting as synced:", error);
    throw error;
  }
}

/**
 * Mark meeting sync as failed
 * Increments retry count
 */
export async function markMeetingFailed(id: string, error: string): Promise<void> {
  try {
    const meeting = await getOfflineMeeting(id);
    if (!meeting) {
      console.error("[Meeting Storage] Meeting not found for failure marking:", id);
      return;
    }

    meeting.retry_count = (meeting.retry_count || 0) + 1;
    meeting.sync_status = "failed";
    meeting.last_error = error;
    meeting.updated_at = new Date().toISOString();

    await saveOfflineMeeting(meeting);
  } catch (error) {
    console.error("[Meeting Storage] Error marking meeting as failed:", error);
    throw error;
  }
}

/**
 * Delete an offline meeting
 */
export async function removeOfflineMeeting(
  id: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await deleteOfflineMeeting(id);
    console.log("[Meeting Storage] Offline meeting deleted:", id);
    return { success: true };
  } catch (error) {
    console.error("[Meeting Storage] Error deleting offline meeting:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Failed to delete offline meeting";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get meeting statistics
 */
export async function getMeetingStats(): Promise<{
  total: number;
  pending: number;
  syncing: number;
  synced: number;
  failed: number;
}> {
  try {
    const meetings = await getAllOfflineMeetings();

    return {
      total: meetings.length,
      pending: meetings.filter((m) => m.sync_status === "pending").length,
      syncing: meetings.filter((m) => m.sync_status === "syncing").length,
      synced: meetings.filter((m) => m.sync_status === "synced").length,
      failed: meetings.filter((m) => m.sync_status === "failed").length,
    };
  } catch (error) {
    console.error("[Meeting Storage] Error getting meeting stats:", error);
    return { total: 0, pending: 0, syncing: 0, synced: 0, failed: 0 };
  }
}
